import { Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  Firestore,
  setDoc,
  Timestamp,
} from '@angular/fire/firestore';
import { ShipInterface } from '../../core/models';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ShipService {
  private shipCollection: ReturnType<typeof collection>;

  constructor(private firestore: Firestore) {
    this.shipCollection = collection(this.firestore, 'ships');
  }

  // Create a new ship with a generated ID
  createNewShip(ship: Omit<ShipInterface, 'id'>): Promise<void> {
    const newDocRef = doc(this.shipCollection); // Generate a new document reference with a unique ID
    const id = newDocRef.id; // Extract the generated ID
    // Explicitly construct the ship object with the new ID
    const shipWithId: ShipInterface = {
      id: id,
      shipName: ship.shipName,
      companyName: ship.companyName,
      createdDate: Timestamp.fromDate(new Date()),
      lastEditDate: Timestamp.fromDate(new Date()),
      // Spread other properties from the input
    };

    return setDoc(newDocRef, shipWithId); // Save the ship with the ID in Firestore
  }

  // Get all ships
  getShips(): Observable<ShipInterface[]> {
    return collectionData(this.shipCollection, {
      idField: 'id',
    }).pipe(
      // Cast to ShipInterface[] first to fix the typing issue
      map((ships) => ships as ShipInterface[]),
      map((ships: ShipInterface[]) => {
        return ships.sort((a, b) => {
          // First compare by company
          const shipNameComparison = (a.shipName || '').localeCompare(
            b.shipName || '',
          );

          // If companies are the same, compare by ship name
          if (shipNameComparison === 0) {
            return (a.companyName || '').localeCompare(b.companyName || '');
          }

          // Otherwise, return the company comparison result
          return shipNameComparison;
        });
      }),
    ) as Observable<ShipInterface[]>;
  }

  // Get a single ship by ID
  getShipData(id: string): Observable<ShipInterface> {
    const shipDocRef = doc(this.firestore, 'ships', id);
    return docData(shipDocRef) as Observable<ShipInterface>;
  }

  // Update a ship
  updateShip(ship: ShipInterface): Promise<void> {
    ship.lastEditDate = Timestamp.fromDate(new Date());
    if (!ship.createdDate) {
      ship.createdDate = Timestamp.fromDate(new Date());
    }
    const shipDoc = doc(this.firestore, `ships/${ship.id}`);
    return setDoc(shipDoc, ship);
  }

  // Delete a ship
  deleteShip(shipId: string): Promise<void> {
    const shipDoc = doc(this.firestore, `ships/${shipId}`);
    return deleteDoc(shipDoc);
  }
}
